// FTEDN — Voting store (Zustand)
import { create } from 'zustand';
import { apiClient } from '../hooks/useApi';

export interface VoteCard {
  id: string;
  title: string;
  description: string;
  category: string;
  votes: number;
  hot: boolean;
  userVoted: boolean;
  createdAt?: string;
}

export interface VoteIdea {
  id: string;
  title: string;
  description: string;
  category: string;
  createdAt: string;
}

interface VotingState {
  cards: VoteCard[];
  loading: boolean;
  error: string | null;
  fetchCards: () => Promise<void>;
  castVote: (cardId: string) => Promise<boolean>;
  submitIdea: (idea: { title: string; description: string; category: string }) => Promise<boolean>;
}

/** Map raw Firestore voting card doc */
export function mapVoteCard(raw: Record<string, unknown>): VoteCard {
  const votes = Number(raw.votes ?? raw.voteCount ?? raw.totalVotes ?? 0);

  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    description: String(raw.description ?? raw.desc ?? ''),
    category: String(raw.category ?? 'product'),
    votes,
    hot: Boolean(raw.hot ?? (votes >= 150)),
    userVoted: Boolean(raw.userVoted ?? false),
    createdAt: raw.createdAt as string | undefined,
  };
}

export const useVotingStore = create<VotingState>((set, get) => ({
  cards: [],
  loading: false,
  error: null,

  fetchCards: async () => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get('/voting/cards');
      if (res.success && Array.isArray(res.data)) {
        set({ cards: res.data.map(mapVoteCard), loading: false });
      } else {
        set({ error: 'Failed to load voting cards', loading: false });
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  castVote: async (cardId) => {
    try {
      const res = await apiClient.post('/voting/vote', { cardId });
      if (res.success) {
        const totalVotes = Number(res.data?.totalVotes);
        set({
          cards: get().cards.map(c =>
            c.id === cardId ? { ...c, votes: Number.isFinite(totalVotes) ? totalVotes : c.votes + 1, userVoted: true } : c
          ),
        });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  submitIdea: async (idea) => {
    try {
      const res = await apiClient.post('/voting/ideas', idea);
      return res.success === true;
    } catch {
      return false;
    }
  },
}));
